import { z } from 'zod';
import { AuditedFields, CursorPageQuery, IsoDateTime, Uuid, Version } from './common.js';
/** Types de documents (FR-DOC-01). */
export const DocumentType = z.enum([
    'IDENTITY_DOCUMENT',
    'POLICE_FORM',
    'CONTRACT',
    'PURCHASE_ORDER',
    'PAYMENT_RECEIPT',
    'CONFIRMATION_PDF',
    'PROFORMA_PDF',
    'INVOICE_PDF',
    'CREDIT_NOTE_PDF',
    'PHOTO',
    'OTHER',
]);
/** Sous-type de pièce d'identité (D-013). */
export const IdentityDocumentKind = z.enum(['CNIB', 'PASSPORT', 'RESIDENCE_PERMIT', 'OTHER']);
export const DocumentStatus = z.enum([
    'PENDING_UPLOAD',
    'PENDING_SCAN',
    'CLEAN',
    'REJECTED',
    'DELETED',
]);
/** Types MIME acceptés à l'upload (validation magic bytes côté serveur). */
export const ALLOWED_UPLOAD_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'application/pdf',
];
export const MAX_UPLOAD_SIZE_BYTES = 15 * 1024 * 1024;
export const DocumentSummary = z.object({
    id: Uuid,
    type: DocumentType,
    identityKind: IdentityDocumentKind.nullable(),
    originalFilename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    status: DocumentStatus,
    /** Sensible = pièce d'identité : accès journalisé, permission document.view_identity. */
    isSensitive: z.boolean(),
    uploadedByName: z.string().nullable(),
    uploadedAt: IsoDateTime,
    expiresAt: IsoDateTime.nullable(),
    customerId: Uuid.nullable(),
    reservationId: Uuid.nullable(),
    companyId: Uuid.nullable(),
    label: z.string().nullable(),
});
export const Document = DocumentSummary.extend({
    sha256: z.string().nullable(),
    notes: z.string().nullable(),
    version: Version,
}).merge(AuditedFields);
/** Étape 1 : intention d'upload → URL pré-signée PUT (ADR-005). */
export const CreateUploadIntentRequest = z.object({
    type: DocumentType.exclude(['CONFIRMATION_PDF', 'PROFORMA_PDF', 'INVOICE_PDF']),
    identityKind: IdentityDocumentKind.nullable().optional(),
    originalFilename: z.string().min(1).max(255),
    mimeType: z.enum(ALLOWED_UPLOAD_MIME_TYPES),
    sizeBytes: z.number().int().positive().max(MAX_UPLOAD_SIZE_BYTES),
    customerId: Uuid.nullable().optional(),
    reservationId: Uuid.nullable().optional(),
    companyId: Uuid.nullable().optional(),
    label: z.string().max(120).nullable().optional(),
    expiresAt: IsoDateTime.nullable().optional(),
    /** Item de conformité à rattacher automatiquement (sinon déduit du type + réservation). */
    complianceItemId: Uuid.nullable().optional(),
});
export const UploadIntent = z.object({
    documentId: Uuid,
    /** URL PUT pré-signée (5 min) ; en dev sans S3 : endpoint API de dépôt direct. */
    uploadUrl: z.string().url(),
    method: z.enum(['PUT', 'POST']),
    headers: z.record(z.string()),
    expiresAt: IsoDateTime,
});
/** Étape 2 : finalisation (vérification taille/MIME/hash, scan antivirus extensible). */
export const CompleteUploadRequest = z.object({
    sha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
});
export const DownloadLink = z.object({
    url: z.string().url(),
    expiresAt: IsoDateTime,
    mimeType: z.string(),
    filename: z.string(),
});
export const ListDocumentsQuery = CursorPageQuery.extend({
    type: DocumentType.optional(),
    customerId: Uuid.optional(),
    reservationId: Uuid.optional(),
    companyId: Uuid.optional(),
    status: DocumentStatus.optional(),
});
export const UpdateDocumentRequest = z.object({
    label: z.string().max(120).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    expiresAt: IsoDateTime.nullable().optional(),
    identityKind: IdentityDocumentKind.nullable().optional(),
    version: Version,
});
export const DeleteDocumentRequest = z.object({ reason: z.string().min(5).max(500) });
export const DocumentAccessAction = z.enum(['VIEW', 'DOWNLOAD', 'UPLOAD', 'DELETE']);
export const DocumentAccessLogEntry = z.object({
    id: Uuid,
    documentId: Uuid,
    action: DocumentAccessAction,
    userName: z.string().nullable(),
    ip: z.string().nullable(),
    occurredAt: IsoDateTime,
});
/** Types de documents dont l'accès est journalisé nominativement (SECURITY §6.1). */
export const SENSITIVE_DOCUMENT_TYPES = ['IDENTITY_DOCUMENT', 'POLICE_FORM'];
export const isSensitiveDocumentType = (type) => SENSITIVE_DOCUMENT_TYPES.includes(type);
//# sourceMappingURL=documents.js.map